import type {
  KnowledgeBaseArticleSummary,
  PagedPayload,
} from "@noverik/contracts";
import { BookOpen, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest, ApiRequestError } from "../lib/api";

export function KnowledgeBasePage() {
  const [data, setData] = useState<PagedPayload<KnowledgeBaseArticleSummary> | null>(null);
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ page: String(page), limit: "24" });
    if (query) params.set("q", query);

    try {
      const payload = await apiRequest<PagedPayload<KnowledgeBaseArticleSummary>>(
        "/agent/knowledge-base?" + params.toString(),
      );
      setData(payload);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Não foi possível carregar a base de conhecimento.",
      );
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setQuery(input.trim());
  };

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">CONHECIMENTO</span>
          <h1>Base de conhecimento</h1>
          <p>Consulte procedimentos, respostas e artigos cadastrados no osTicket.</p>
        </div>
      </div>

      <form className="searchbox inline kb-search" onSubmit={submit}>
        <Search size={17} />
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Buscar por pergunta, conteúdo, palavra-chave ou categoria..."
        />
        <button className="search-submit" type="submit">Buscar</button>
      </form>

      {error && <div className="data-message error">{error}</div>}

      {!error && (
        <div className="kb-grid">
          {loading && <div className="data-message">Carregando artigos...</div>}
          {!loading && data?.items.length === 0 && (
            <div className="data-message">Nenhum artigo encontrado.</div>
          )}
          {!loading && data?.items.map((article) => (
            <Link to={"/desk/base/" + article.id} className="kb-card" key={article.id}>
              <span className="meta-icon blue"><BookOpen size={19} /></span>
              <div className="kb-card-copy">
                <div className="kb-card-meta">
                  <span>{article.category?.name || "Sem categoria"}</span>
                  <span className={article.published ? "status status-open" : "status status-closed"}>
                    {article.visibility || (article.published ? "Público" : "Interno")}
                  </span>
                </div>
                <strong>{article.question}</strong>
                <p>{article.teaser || "Sem resumo disponível."}</p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {!loading && data && data.pages > 1 && (
        <div className="pagination">
          <span>Página {data.page} de {data.pages}</span>
          <div>
            <button className="button secondary" disabled={data.page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
              <ChevronLeft size={16} /> Anterior
            </button>
            <button className="button secondary" disabled={data.page >= data.pages} onClick={() => setPage((value) => value + 1)}>
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
